import { useId, useMemo } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

interface TablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPageChange: (next: number) => void;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

const DEFAULT_PAGE_SIZES: number[] = [10, 25, 50, 100];

/**
 * Right-aligned pagination control with rows-per-page selector.
 * Inputs are clamped before propagation:
 *  - page is forced into `[1, totalPages]`
 *  - pageSize must be > 0; invalid values are ignored
 *  - total < 0 is treated as 0
 *
 * Renders nothing when `total === 0`.
 */
export function TablePagination({
  page,
  pageSize,
  total,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  className,
}: TablePaginationProps): JSX.Element | null {
  const safeTotal = Math.max(0, Math.floor(total));
  const safePageSize = pageSize > 0 ? Math.floor(pageSize) : 0;
  const totalPages = safePageSize === 0 ? 1 : Math.max(1, Math.ceil(safeTotal / safePageSize));
  const safePage = useMemo(() => Math.min(Math.max(1, Math.floor(page)), totalPages), [page, totalPages]);

  const pageSizeId = useId();

  if (safeTotal === 0) return null;

  const goTo = (next: number): void => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    if (clamped !== safePage) onPageChange(clamped);
  };

  return (
    <nav aria-label="Pagination" className={clsx('inline-flex items-center gap-4', className)}>
      {onPageSizeChange && (
        <label htmlFor={pageSizeId} className="inline-flex items-center gap-2 text-xs" style={{ color: 'var(--bui-text-secondary)' }}>
          <span>Rows per page</span>
          <select
            id={pageSizeId}
            value={safePageSize || pageSizeOptions[0]}
            onChange={(e) => {
              const nextSize = Number(e.target.value);
              if (Number.isFinite(nextSize) && nextSize > 0) onPageSizeChange(nextSize);
            }}
            className="h-7 rounded-md border bg-white px-2 text-xs focus-visible:outline-none focus-visible:ring-2"
            style={{
              borderColor: 'var(--bui-border)',
              color: 'var(--bui-text-primary)',
            }}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      )}

      <span className="text-xs" style={{ color: 'var(--bui-text-secondary)' }}>
        {safePage} of {totalPages}
      </span>

      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={safePage <= 1}
          onClick={() => goTo(safePage - 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[var(--bui-surface-hover)] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
          style={{ color: 'var(--bui-text-secondary)' }}
        >
          <ChevronLeftIcon />
        </button>
        <button
          type="button"
          aria-label="Next page"
          disabled={safePage >= totalPages}
          onClick={() => goTo(safePage + 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[var(--bui-surface-hover)] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
          style={{ color: 'var(--bui-text-secondary)' }}
        >
          <ChevronRightIcon />
        </button>
      </span>
    </nav>
  );
}
